import { useCallback, useEffect, useRef } from 'react'

// useOverlayHistory — make a fullscreen overlay part of session history.
//
// Opening an artifact was pure component state, so the browser's Back gesture
// knew nothing about it: pressing Back from a fullscreen artifact skipped
// straight past it and left the view entirely, losing the thing you were
// looking at. On a trackpad, where Back is a two-finger swipe, that is very
// easy to do by accident.
//
// Opening pushes an entry, so Back pops it and simply closes the overlay.
// Closing by a control retracts that entry, so Back never has to be pressed
// twice to leave the page.
export function useOverlayHistory(open: boolean, close: () => void) {
  // The close callback is usually an inline arrow, so keep it in a ref rather
  // than letting a new identity re-run the effect and push a second entry.
  const closeRef = useRef(close)
  closeRef.current = close
  const pushed = useRef(false)

  useEffect(() => {
    if (!open) return
    // Once per opening, not once per effect run — StrictMode runs this twice.
    if (!pushed.current) {
      window.history.pushState({ xnapzOverlay: true }, '')
      pushed.current = true
    }

    const onPop = () => {
      // Our entry is the one being popped — the overlay is what Back meant.
      pushed.current = false
      closeRef.current()
    }
    // A route change while an overlay is open would strand its entry, so the
    // overlay closes with the navigation.
    const onHash = () => closeRef.current()

    window.addEventListener('popstate', onPop)
    window.addEventListener('hashchange', onHash)
    return () => {
      window.removeEventListener('popstate', onPop)
      window.removeEventListener('hashchange', onHash)
      // Deliberately NOT retracting the entry here. React re-invokes effects on
      // every mount (StrictMode does it twice in development), and calling
      // history.back() from cleanup popped the entry we had just pushed — which
      // closed the overlay the instant it opened. Retraction belongs to the
      // explicit dismiss path below, which is the only way it is ever closed.
    }
  }, [open])

  /** Dismiss the overlay the same way Back would, so history stays balanced. */
  return useCallback(() => {
    if (pushed.current) {
      pushed.current = false
      window.history.back()
    }
    closeRef.current()
  }, [])
}
